import { TWAPOrderRow } from '@/features/order/chat/types/chat-order'
import { safeParseFloat } from '@/lib/utils'
import { Frequency, OrderSize, TwapAlgoParams } from '@/types/generated/algo'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import { Exchange, TradeVenue } from '@/types/generated/common/types'

// ============================================================================
// Types
// ============================================================================

export interface TwapCardData {
  /** Quantity distribution mode */
  quantity: 'random' | 'fixed'
  /** Schedule interval in milliseconds */
  schedule: number
  /** Total duration in milliseconds */
  duration: number
  /** Orders to execute */
  orders: TWAPOrderRow[]
}

export interface TransformOptions {
  /** Account ID for the orders */
  accountId: string
  /** Variance percentage for random mode (default: 0.2 = 20%) */
  randomVariance?: number
}

// ============================================================================
// Order Form Types (for direct form input)
// ============================================================================

export interface TwapOrderFormData {
  /** Trading symbol */
  symbol: string
  /** Total quantity to execute */
  quantity: string
  /** Order side */
  side: 'buy' | 'sell'
  /** Unit denomination */
  unit: string
  /** Fixed frequency in ms (optional) */
  frequencyFixed?: number
  /** Random frequency range (optional) */
  frequencyRandom?: { min: number; max: number }
  /** Fixed order size (optional) */
  orderSizeFixed?: string
  /** Random order size range (optional) */
  orderSizeRandom?: { min: string; max: string }
}

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Creates a Frequency object based on schedule and mode
 */
function createFrequency(scheduleMs: number, isRandom: boolean, variance = 0.2): Frequency {
  if (isRandom) {
    const min = Math.floor(scheduleMs * (1 - variance))
    const max = Math.ceil(scheduleMs * (1 + variance))
    return {
      fixed: undefined,
      random: {
        min: String(min),
        max: String(max),
      },
    }
  }
  return {
    fixed: String(scheduleMs),
    random: undefined,
  }
}

/**
 * Creates an OrderSize object based on size and mode
 */
function createOrderSize(size: string, isRandom: boolean, variance = 0.2): OrderSize {
  const sizeNum = safeParseFloat(size)
  if (isNaN(sizeNum) || sizeNum <= 0) {
    return { fixed: size, random: undefined }
  }

  if (isRandom) {
    const min = (sizeNum * (1 - variance)).toFixed(8)
    const max = (sizeNum * (1 + variance)).toFixed(8)
    return {
      fixed: undefined,
      random: { min, max },
    }
  }
  return {
    fixed: size,
    random: undefined,
  }
}

/**
 * Converts unit string to Denomination enum
 */
function unitToDenomination(unit: string): Denomination {
  const upperUnit = unit.toUpperCase()
  if (upperUnit === 'USD' || upperUnit === 'USDT' || upperUnit === 'USDC') {
    return Denomination.DENOMINATION_QUOTE
  }
  return Denomination.DENOMINATION_BASE
}

/**
 * Converts side string to OrderSide enum
 */
function sideToOrderSide(side: 'buy' | 'sell'): OrderSide {
  return side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL
}

/**
 * Transforms a single TWAPOrderRow to TwapAlgoParams
 */
export function transformOrderToTwapParams(
  order: TWAPOrderRow,
  cardData: Pick<TwapCardData, 'quantity' | 'schedule'>,
  options: TransformOptions
): TwapAlgoParams {
  const isRandom = cardData.quantity === 'random'
  const variance = options.randomVariance ?? 0.2

  return {
    symbol: order.symbol,
    quantity: order.size,
    cadence: {
      frequency: createFrequency(cardData.schedule, isRandom, variance),
      orderSize: createOrderSize(order.size, isRandom, variance),
    },
    orderSide: sideToOrderSide(order.side),
    accountId: options.accountId,
    denomination: unitToDenomination(order.unit),
    // Left unspecified on purpose: public-api overwrites both from the account's own venue
    // on every algo start (stamp_start_algo_params_for_account), so nothing sent here is read.
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
  }
}

/**
 * Transforms all orders from TWAP card data to TwapAlgoParams array
 */
export function transformToTwapAlgoParams(
  data: TwapCardData,
  options: TransformOptions
): TwapAlgoParams[] {
  return data.orders.map((order) =>
    transformOrderToTwapParams(order, { quantity: data.quantity, schedule: data.schedule }, options)
  )
}

/**
 * Transforms order form data to TwapAlgoParams
 * Only includes fields that are explicitly provided (no arbitrary defaults)
 */
export function transformFormToTwapParams(
  formData: TwapOrderFormData,
  accountId: string
): TwapAlgoParams {
  // Build frequency only if provided
  let frequency: Frequency | undefined
  if (formData.frequencyFixed !== undefined) {
    frequency = { fixed: String(formData.frequencyFixed), random: undefined }
  } else if (formData.frequencyRandom) {
    frequency = {
      fixed: undefined,
      random: {
        min: String(formData.frequencyRandom.min),
        max: String(formData.frequencyRandom.max),
      },
    }
  }

  // Build orderSize only if provided
  let orderSize: OrderSize | undefined
  if (formData.orderSizeFixed !== undefined) {
    orderSize = { fixed: formData.orderSizeFixed, random: undefined }
  } else if (formData.orderSizeRandom) {
    orderSize = {
      fixed: undefined,
      random: {
        min: formData.orderSizeRandom.min,
        max: formData.orderSizeRandom.max,
      },
    }
  }

  return {
    symbol: formData.symbol,
    quantity: formData.quantity,
    cadence: {
      frequency,
      orderSize,
    },
    orderSide: sideToOrderSide(formData.side),
    accountId,
    denomination: unitToDenomination(formData.unit),
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
  }
}

/**
 * Calculates the number of expected slices based on duration and schedule
 */
export function calculateExpectedSlices(durationMs: number, scheduleMs: number): number {
  if (scheduleMs <= 0) return 0
  return Math.floor(durationMs / scheduleMs)
}

/**
 * Formats duration in milliseconds to human-readable string
 */
export function formatDuration(durationMs: number): string {
  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h`
  } else if (minutes > 0) {
    return `${minutes}m`
  }
  return `${Math.floor(durationMs / 1000)}s`
}

/**
 * Formats schedule interval to human-readable string
 */
export function formatSchedule(scheduleMs: number): string {
  if (scheduleMs >= 1000 * 60 * 60) {
    return `${(scheduleMs / (1000 * 60 * 60)).toFixed(1)}h`
  } else if (scheduleMs >= 1000 * 60) {
    return `${(scheduleMs / (1000 * 60)).toFixed(0)}m`
  }
  return `${(scheduleMs / 1000).toFixed(0)}s`
}
